/**
 * Game audio: the engine model (also the source of the HUD's rpm and gear), bike voice, one-shots, listener at the
 * chase camera, and the volume buses the settings screen controls. Same wiring as src/dev/audio-hook.ts, but driven
 * by the session's frames instead of its own loop and key listeners.
 */
import { sampleSetPath } from '../audio/manifest';
import type { BikeState } from '../sim/bike';
import type { Surface } from '../sim/terrain';
import { add, rotate, vec } from '../sim/math';
import { AudioEngine, BikeVoice, LevelKind } from '../audio/engine';
import type { BikeConfig } from '../sim/config';
import type { BikeSpec } from '../sim/bikes';
import {
  EngineConfig, DEFAULT_ENGINE, createEngineState, engineConfigFor, createTriggers, frameFromBike, rollGain, skidGain, stepEngine, stepTriggers,
} from '../audio/model';
import type { Settings } from '../ui/logic/settings';

export class GameAudio {
  engine: ReturnType<typeof createEngineState>;
  /** The audio engine model of this bike: same gears and shift points as its physics config. */
  private engineCfg: EngineConfig;
  private pitch: number;
  private set = '';
  private loops: 'short' | 'full' = 'full';
  private bike: BikeSpec | undefined;
  private readonly audio: AudioEngine;
  private readonly voice: BikeVoice | null;
  private readonly triggers = createTriggers();
  private readonly history = { compression: 0, speed: 0 };
  private current: BikeState | null = null;

  constructor(level: string, bike?: BikeSpec, cfg?: BikeConfig) {
    this.engineCfg = cfg ? engineConfigFor(cfg) : DEFAULT_ENGINE;
    this.engine = createEngineState(this.engineCfg);
    this.pitch = bike?.audio.pitch ?? 1;
    this.audio = AudioEngine.create();
    this.voice = this.audio.ctx ? this.audio.createBike(1) : null;
    if (!this.audio.ctx) return;
    this.audio.unlockOn(window);
    this.audio.setLevel((['ring', 'track', 'enduro'].includes(level) ? level : 'ring') as LevelKind);
    // Each fleet bike picks its sample set: 2-strokes public/audio/bikes/2t (scripts/fetch/audio2t.mjs), 4-strokes
    // public/audio/bikes/4t (scripts/fetch/audio4t.mjs). ?engine=2t|4t overrides it for listening tests. A set that
    // fails to load keeps the procedural engine (AudioEngine.loadSamples).
    void this.loadSet(bike);
  }

  private loadSet(bike?: BikeSpec, force = false): Promise<unknown> {
    this.bike = bike;
    const set = sampleSetPath(bike?.audio.set ?? '4t', new URLSearchParams(location.search).get('engine'));
    if ((set === this.set && !force) || !this.audio.ctx) return Promise.resolve();
    this.set = set;
    return this.audio.loadSamples(`${import.meta.env.BASE_URL}${set}`, this.loops === 'short');
  }

  /** Engine loop set (the `audio` world module): `short` keeps idle and two rpm loops, `full` every loop. */
  async setLoopSet(s: 'short' | 'full'): Promise<void> {
    if (s === this.loops) return;
    this.loops = s;
    await this.loadSet(this.bike, true);
  }

  /** Another fleet bike: its gears, pitch and (2T/4T) sample set; the voice swaps engines when the set is in. */
  setBike(bike: BikeSpec, cfg: BikeConfig): void {
    this.engineCfg = engineConfigFor(cfg);
    this.engine = createEngineState(this.engineCfg);
    this.pitch = bike.audio.pitch;
    void this.loadSet(bike);
  }

  /** Bus gains from the settings (master is the user's level times the engine's own 0.8 headroom). */
  apply(s: Settings): void {
    const a = this.audio;
    if (!a.ctx || !a.buses) return;
    const t = a.ctx.currentTime;
    a.buses.master.gain.setTargetAtTime(s.master, t, 0.05);
    a.buses.engine.gain.setTargetAtTime(s.engine, t, 0.05);
    a.buses.sfx.gain.setTargetAtTime(s.sfx, t, 0.05);
    a.buses.ambience.gain.setTargetAtTime(s.ambience, t, 0.05);
  }

  /** Suspend on pause so nothing drones under the menu. */
  setPaused(p: boolean): void {
    const ctx = this.audio.ctx;
    if (!ctx) return;
    if (p) void ctx.suspend(); else void ctx.resume();
  }

  frame(b: BikeState, throttle: number, surface: Surface, dt: number): void {
    dt = Math.min(0.1, dt);
    if (dt <= 0) return;
    if (b !== this.current) {
      this.current = b;
      this.history.compression = Math.max(b.wheels[0].compression, b.wheels[1].compression);
      this.history.speed = b.telemetry.speed;
    }
    const f = frameFromBike(b, throttle, surface, this.history, dt);
    stepEngine(this.engine, f, dt, this.engineCfg);
    if (!this.audio.running) return;
    const ev = stepTriggers(this.triggers, f, dt);
    this.voice?.update({ rpm: this.engine.rpm * this.pitch, load: this.engine.load, skid: skidGain(f.rearSlip, f.lateralSpeed), roll: rollGain(f.speed, f.airborne), surface: f.surface }, b.position);
    if (ev.landing) this.audio.landing(ev.landing, b.position);
    if (ev.splash) this.audio.splash(ev.splash, b.position);
    const fwd = rotate(b.orientation, vec(0, 0, 1));
    this.audio.setListener(add(b.position, rotate(b.orientation, vec(0, 2, -6))), fwd);
    this.audio.tick(dt, b.position);
  }

  private tapNode: MediaStreamAudioDestinationNode | null = null;

  /** The mix as a MediaStream (the cinematic video records it); null without WebAudio. */
  tap(): MediaStream | null {
    const a = this.audio;
    if (!a.ctx || !a.buses) return null;
    void a.ctx.resume();
    this.tapNode ??= a.ctx.createMediaStreamDestination();
    a.buses.master.connect(this.tapNode);
    return this.tapNode.stream;
  }

  untap(): void {
    if (this.tapNode && this.audio.buses) try { this.audio.buses.master.disconnect(this.tapNode); } catch { /* not connected */ }
  }

  /** After a teleport (respawn), so the jump is not heard as a landing. */
  resetHistory(): void { this.current = null; }
}
